import { useAppDispatch, useAppSelector } from 'hooks/redux';
// ROOT
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

// COMPONENTS
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import LoadingView from 'components/LoadingView';
import ZeroStateView from 'components/ZeroStateView';

// MUI
import { Typography } from '@mui/material';

import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsSelectors } from 'app-components/flux/accounts';
import { freeToSpendTypes } from 'app-components/flux/free-to-spend';
import { loadState, useLoadState } from 'app-components/hooks/utils';
// APP-COMPONENTS
import { tracker } from 'app-components/utils/core';

import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import ConnectFIs from 'assets/illustrations/ConnectFIs.svg';
import NoFIs from 'assets/illustrations/NoFIs.svg';
// ASSETS
import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
import ChartDemo from 'assets/images/spendingPlan/ChartDemo.svg';
import SPButtonDemo from 'assets/images/spendingPlan/SPButtonDemo.svg';
import SpendingPlanImg from 'assets/images/spendingPlan/SpendingPlanImg.svg';
import SimpleContentView from '../SimpleContentView';
export const introIndexEnums = Object.freeze({
  INTRO_ONE: 'INTRO_ONE',
  INTRO_TWO: 'INTRO_TWO',
  INTRO_THREE: 'INTRO_THREE',
  ADD_ACCOUNTS: 'ADD_ACCOUNTS',
  CONNECT_ACCOUNTS: 'CONNECT_ACCOUNTS',
});

export type IntroIndex = (typeof introIndexEnums)[keyof typeof introIndexEnums];
const { GSFStageEnum } = freeToSpendTypes;

// Image Components!
const introOneImg = <img key={'imageOne'} width='168px' height='168px' src={SpendingPlanImg} />;
const introTwoImg = <img key={'imageTwo'} width='368px' height='164px' src={ChartDemo} />;
const introThreeImg = <img key={'imageThree'} width='224px' height='291px' src={SPButtonDemo} />;
const addAccountsImg = <img key={'imageFour'} width='168px' height='168px' src={ConnectFIs} />;
const connectAccountsImg = <img key={'imageFive'} width='168px' height='168px' src={NoFIs} />;

function Intro() {
  const [_gettingStartedFlowPhase, setGettingStartedFlowPhase] = usePreferenceV2(
    'sSPGettingStartedFlowPhase',
    GSFStageEnum.COMPLETE,
  );
  const [screenIndex, setScreenIndex] = useState<IntroIndex>(introIndexEnums.INTRO_ONE);
  const [addAccountButtonPressed, setAddAccountButtonPressed] = useState(false);
  const dispatch = useAppDispatch();

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);

  const connectedAccounts = accountsById?.filter((account) => account?.aggregators)?.size ?? 0;

  //  ================= VIEW STATE LOGIC =====================
  //  Views
  const accountsView = useLoadState(accountsSelectors);

  const mainView = useMemo(() => {
    const showSkeletonView = Boolean(loadState.skeletonStates.includes(accountsView));

    const tryAgainStates = Boolean(loadState.tryAgainStates.includes(accountsView));
    const showTryAgainView = Boolean(tryAgainStates || loadState.updateRequiredStates.includes(accountsView));

    const showReadyView = Boolean(loadState.readyStates.includes(accountsView));

    let myMainView = '';
    if (showReadyView) {
      myMainView = 'READY';
    } else if (showTryAgainView) {
      myMainView = 'FAILED';
    } else if (showSkeletonView) {
      myMainView = 'LOADING';
    }

    return myMainView;
  }, [accountsView]);

  useEffect(() => {
    tracker.track(tracker.events.viewSpendingPlanOnboardingV2);
  }, []); // eslint-disable react-hooks/exhaustive-deps

  // If a user enters the add account flow and successfully connects accounts
  useEffect(() => {
    if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS && connectedAccounts > 0) {
      setScreenIndex(introIndexEnums.ADD_ACCOUNTS);
    }
  }, [connectedAccounts, screenIndex]);

  // ================ ACTION HANDLING =======================

  const onClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog());
  }, [dispatch]);

  const closeAndContinue = useCallback(() => {
    tracker.track(tracker.events.completeSpendingPlanIntro, { accounts_added: addAccountButtonPressed });
    setGettingStartedFlowPhase(GSFStageEnum.INCOME);
    onClose();
  }, [addAccountButtonPressed, onClose, setGettingStartedFlowPhase]);

  const addAccountFlow = useCallback(() => {
    dispatch(
      accountDiscoveryActions.doAccountDiscovery({
        initiator: 'spending plan GSF',
        mode: 'ADD-FI',
      }),
    );
  }, [dispatch]);

  const onContinue = useCallback(() => {
    if (screenIndex === introIndexEnums.INTRO_ONE) {
      setScreenIndex(introIndexEnums.INTRO_TWO);
    }
    if (screenIndex === introIndexEnums.INTRO_TWO) {
      setScreenIndex(introIndexEnums.INTRO_THREE);
    }
    if (screenIndex === introIndexEnums.INTRO_THREE) {
      if (connectedAccounts > 0) {
        setScreenIndex(introIndexEnums.ADD_ACCOUNTS);
      } else {
        setScreenIndex(introIndexEnums.CONNECT_ACCOUNTS);
      }
    }
    if (screenIndex === introIndexEnums.ADD_ACCOUNTS) {
      closeAndContinue();
    }
    if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) {
      setAddAccountButtonPressed(true);
      addAccountFlow();
    }
  }, [screenIndex, connectedAccounts, closeAndContinue, addAccountFlow]);

  const onBack = useCallback(() => {
    if (screenIndex === introIndexEnums.INTRO_TWO) {
      setScreenIndex(introIndexEnums.INTRO_ONE);
    }
    if (screenIndex === introIndexEnums.INTRO_THREE) {
      setScreenIndex(introIndexEnums.INTRO_TWO);
    }
  }, [screenIndex]);

  const onSecondaryButtonPress = useCallback(() => {
    if (screenIndex === introIndexEnums.ADD_ACCOUNTS) {
      setAddAccountButtonPressed(true);
      addAccountFlow();
    }
    if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) {
      closeAndContinue();
    }
  }, [addAccountFlow, closeAndContinue, screenIndex]);

  // =================== RENDERING ===================

  const disableContinue = false;
  let continueText = '';
  if (screenIndex === introIndexEnums.INTRO_ONE) continueText = 'See how it works';
  if (screenIndex === introIndexEnums.INTRO_TWO) continueText = 'Next';
  if (screenIndex === introIndexEnums.INTRO_THREE) continueText = 'Get Started';
  if (screenIndex === introIndexEnums.ADD_ACCOUNTS) continueText = "I'm Ready";
  if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) continueText = 'Connect Accounts';

  let secondaryText = '';
  let secondaryTextVariant: 'text' | 'contained' | 'outlined' | undefined = undefined;
  if (screenIndex === introIndexEnums.INTRO_ONE) secondaryText = '';
  if (screenIndex === introIndexEnums.INTRO_TWO) secondaryText = '';
  if (screenIndex === introIndexEnums.INTRO_THREE) secondaryText = '';
  if (screenIndex === introIndexEnums.ADD_ACCOUNTS) secondaryText = 'Connect Accounts';
  if (screenIndex === introIndexEnums.ADD_ACCOUNTS) secondaryTextVariant = 'outlined';
  if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) secondaryText = 'Skip for now';
  if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) secondaryTextVariant = 'text';

  let tertiaryText = '';
  if (screenIndex === introIndexEnums.INTRO_ONE) tertiaryText = '';
  if (screenIndex === introIndexEnums.INTRO_TWO) tertiaryText = 'Back';
  if (screenIndex === introIndexEnums.INTRO_THREE) tertiaryText = 'Back';
  if (screenIndex === introIndexEnums.ADD_ACCOUNTS) tertiaryText = '';
  if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) tertiaryText = '';

  const renderScreen = () => {
    if (mainView === 'LOADING') {
      return <LoadingView size={100} />;
    }
    if (mainView === 'FAILED') {
      return (
        <ZeroStateView
          primaryVariant='displayRegular'
          secondaryVariant='bodyRegular'
          id='gsf-failure'
          primary='Data has failed to load'
          secondary={'Data has failed to load, please refresh and try again'}
          icon={paperAirplaneCrash}
        />
      );
    }
    if (mainView === 'READY') {
      if (screenIndex === introIndexEnums.INTRO_ONE) {
        return (
          <SimpleContentView
            image={introOneImg}
            mainText={'Welcome to Spending Plan!'}
            secondaryText={
              <Typography variant='bodyRegular' color={'textSecondary'}>
                Spending Plan organizes your transactions so you can easily&nbsp;
                <Typography component='span' variant='bodyMedium'>
                  see how your money is spent
                </Typography>{' '}
                and&nbsp;
                <Typography component='span' variant='bodyMedium'>
                  how much you have to work with
                </Typography>
                &nbsp; each month.
              </Typography>
            }
          />
        );
      }
      if (screenIndex === introIndexEnums.INTRO_TWO) {
        return (
          <SimpleContentView
            image={introTwoImg}
            secondaryText={
              <Typography variant='bodyRegular' color={'textSecondary'}>
                We’ll bucket your transactions into 4 groups—&nbsp;
                <Typography component='span' variant='bodyMedium'>
                  Income, Bills, Planned Spend,
                </Typography>{' '}
                and&nbsp;
                <Typography component='span' variant='bodyMedium'>
                  Other Spend
                </Typography>
                .
              </Typography>
            }
          />
        );
      }
      if (screenIndex === introIndexEnums.INTRO_THREE) {
        return (
          <SimpleContentView
            image={introThreeImg}
            secondaryText={
              <Typography variant='bodyRegular' color={'textSecondary'}>
                We’ll subtract Bills, Planned Spend and Other Spend from your Income to show you what you have left each
                month to save or spend!
                <br />
                <br />
                <Typography component='span' variant='bodyMedium'>
                  Use the setup guide at the bottom of the screen
                </Typography>
                &nbsp; to help build your Spending Plan.
              </Typography>
            }
            row
          />
        );
      }
      if (screenIndex === introIndexEnums.ADD_ACCOUNTS) {
        return (
          <SimpleContentView
            image={addAccountsImg}
            mainText={'Before you start...'}
            secondaryText={
              'To create a complete Spending Plan, make sure all of your accounts are connected to Simplifi first. If you haven’t yet, please connect all your accounts now.'
            }
          />
        );
      }
      if (screenIndex === introIndexEnums.CONNECT_ACCOUNTS) {
        return (
          <SimpleContentView
            image={connectAccountsImg}
            mainText={'No accounts connected'}
            secondaryText={
              'Spending Plan uses transactions from connected accounts to create personalized monthly insights. \n' +
              'Let’s connect your accounts before getting started.'
            }
          />
        );
      }
    }
    return null;
  };

  return (
    <StdDialog
      onClose={onClose}
      open={true}
      sharedcomponentid={'FIRST_TIME_USE_DIALOG_V2'}
      showCloseButton
      size='md'
      title={'Spending Plan'}
    >
      <StdDialogContent
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {renderScreen()}
      </StdDialogContent>
      <StdDialogActions
        primary={{
          id: 'primary-button',
          disabled: disableContinue,
          children: continueText,
          onClick: onContinue,
          variant: 'contained',
        }}
        secondary={{
          id: 'secondary-button',
          children: secondaryText,
          variant: secondaryTextVariant,
          onClick: secondaryText ? onSecondaryButtonPress : undefined,
        }}
        tertiaryAction={{
          id: 'tertiary-button',
          children: tertiaryText,
          variant: 'outlined',
          onClick: tertiaryText ? onBack : undefined,
        }}
      />
    </StdDialog>
  );
}

export default Intro;
